import {useGetUser} from '@/modules/users/api'
import {useGetPatient} from '@/modules/patients/api'
import {useGetDrugCatalogItem} from '@/modules/drug-catalog/substances/api'
import {useGetPrescribedName} from '@/modules/products/trade-names/api'
import {UserTag} from '@/shared/components/ui/tags/user-tag'
import {PatientTag} from '@/shared/components/ui/tags/patient-tag'
import {DrugTag} from '@/shared/components/ui/tags/drug-tag'
import {PrescribedNameTag} from '@/shared/components/ui/tags/prescribed-name-tag'
import {Badge} from '@chakra-ui/react'
import {getDatetimeFormatter, getShortDateFormatter} from '@cypro/ui/utils'
import {type ReactNode} from 'react'
import {isDefined} from '@cypro/engine/utils'

const USER_REF_KEYS = [
  'doctorValidatedBy',
  'pharmacistValidatedBy',
  'createdBy',
  'updatedBy',
  'deletedBy',
  'performedBy',
]
const DATE_SUFFIXES = ['At', 'Date'] as const
const FIELD_TAG_MAP = {
  userId: 'user',
  patientId: 'patient',
  activeSubstanceId: 'activeSubstance',
  prescribedNameId: 'prescribedName',
} as const

type TagType = (typeof FIELD_TAG_MAP)[keyof typeof FIELD_TAG_MAP]

function getFieldTagType(key: string): TagType | null {
  for (const [field, tagType] of Object.entries(FIELD_TAG_MAP)) {
    if (key === field || key.endsWith(`/${field}`)) return tagType
  }
  return null
}

function isUserRefKey(key: string): boolean {
  return USER_REF_KEYS.some(refKey => key === refKey || key.endsWith(`/${refKey}`))
}

/** Keys whose id value is shown as a name tag instead of the raw id. */
export function isNamedRefKey(key: string): boolean {
  return isUserRefKey(key) || getFieldTagType(key) !== null
}

export function renderDetailValue(key: string, value: unknown): ReactNode {
  if (!isDefined(value) || value === '') return '-'
  if (typeof value === 'boolean') return value ? 'Yes' : 'No'

  if (DATE_SUFFIXES.some(suffix => key.endsWith(suffix))) {
    const date = typeof value === 'string' || typeof value === 'number' ? new Date(value) : null
    if (!date || Number.isNaN(date.getTime())) return String(value)
    if (date.getUTCHours() === 0 && date.getUTCMinutes() === 0) return getShortDateFormatter().format(date)
    return getDatetimeFormatter().format(date)
  }

  if (typeof value !== 'string') return String(value)

  if (isUserRefKey(key)) return <UserRefCell userId={value} />

  const tagType = getFieldTagType(key)
  if (tagType) {
    switch (tagType) {
      case 'user':
        return <UserRefCell userId={value} />
      case 'patient':
        return <PatientRefCell patientId={value} />
      case 'activeSubstance':
        return <ActiveSubstanceRefCell drugId={value} />
      case 'prescribedName':
        return <PrescribedNameRefCell prescribedNameId={value} />
    }
  }

  return value
}

export function renderDetailBadge(key: string, value: unknown): ReactNode {
  if (value === undefined || value === null || value === '') return null

  const rendered = renderDetailValue(key, value)
  if (rendered === null) return null

  return (
    <Badge variant="outline" backgroundColor="white" whiteSpace="pre-line">
      {rendered}
    </Badge>
  )
}

function UserRefCell({userId, actorName}: {userId: string; actorName?: string}) {
  const [user] = useGetUser(userId)
  return (
    <UserTag user={user} width="max-content" ml="auto">
      {user?.name || actorName || userId}
    </UserTag>
  )
}

function PatientRefCell({patientId}: {patientId: string}) {
  const [patient] = useGetPatient(patientId)
  return <PatientTag patient={patient} width="max-content" ml="auto" />
}

function ActiveSubstanceRefCell({drugId}: {drugId: string}) {
  const [drug] = useGetDrugCatalogItem(drugId)
  return <DrugTag drug={drug} width="max-content" ml="auto" />
}

function PrescribedNameRefCell({prescribedNameId}: {prescribedNameId: string}) {
  const [prescribedName] = useGetPrescribedName(prescribedNameId)
  return <PrescribedNameTag prescribedName={prescribedName} width="max-content" ml="auto" />
}
